import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import FeedbackForm from '../../components/Feedback/FeedbackForm';
import {
  HiDocumentText,
  HiTrash,
  HiPencil,
  HiXMark,
  HiCheckCircle,
  HiChatBubbleLeftEllipsis,
  HiClock,
  HiDocument,
  HiArrowDownTray,
  HiChevronLeft,
  HiChevronRight,
  HiCloudArrowUp,
  HiPlus,
} from 'react-icons/hi2';
import { storage, firestore } from '../../database/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject, getMetadata } from 'firebase/storage';
import { toast } from 'react-hot-toast';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';

const ModeratorReports = () => {
  const { currentUser } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('view');
  const [selectedReport, setSelectedReport] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    attachments: [],
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    reportId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadReports();
    }
  }, [currentUser]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const reportsRef = collection(firestore, 'moderatorReports');
      const q = query(
        reportsRef,
        where('email', '==', currentUser.email),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const userReports = querySnapshot.docs.map(doc => {
        const data = doc.data();
        if (data.fileData && Array.isArray(data.fileData)) {
          data.fileData = data.fileData.map(file => ({
            ...file,
            uploadedAt: file.uploadedAt || file.timeCreated || new Date().toISOString(),
            size: file.size || 0,
            type: file.type || file.contentType || 'application/octet-stream',
          }));
        }
        return { id: doc.id, ...data };
      });
      setReports(userReports);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      const isValidType = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type);
      const isValidSize = file.size <= 10 * 1024 * 1024;

      if (!isValidType) {
        toast.error(`${file.name} is not a valid file type. Please upload PDF or Word documents.`);
      }
      if (!isValidSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
      }

      return isValidType && isValidSize;
    });

    if (validFiles.length === 0) return;

    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setUploading(true);
      const moderatorRef = collection(firestore, 'moderator');
      const q = query(moderatorRef, where('email', '==', currentUser.email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        toast.error('Moderator profile not found');
        return;
      }

      const moderatorData = querySnapshot.docs[0].data();
      const uploadedFilesData = [];
      for (const file of uploadedFiles) {
        try {
          const filePath = `moderatorReports/${currentUser.email}/${Date.now()}_${file.name}`;
          const storageRef = ref(storage, filePath);

          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);

          const metadata = await getMetadata(storageRef);

          uploadedFilesData.push({
            name: file.name,
            url: downloadURL,
            size: metadata.size,
            type: metadata.contentType,
            uploadedAt: (() => {
              if (!metadata.timeCreated) return new Date().toISOString();

              if (typeof metadata.timeCreated === 'string') {
                return new Date(metadata.timeCreated).toISOString();
              } else if (
                metadata.timeCreated &&
                typeof metadata.timeCreated.toDate === 'function'
              ) {
                return metadata.timeCreated.toDate().toISOString();
              } else if (
                metadata.timeCreated &&
                typeof metadata.timeCreated.toMillis === 'function'
              ) {
                return new Date(metadata.timeCreated.toMillis()).toISOString();
              } else {
                try {
                  return new Date(metadata.timeCreated).toISOString();
                } catch (error) {
                  return new Date().toISOString();
                }
              }
            })(),
            path: filePath,
            uploadedBy: {
              email: currentUser.email,
              firstName: moderatorData.firstname || '',
              lastName: moderatorData.lastname || '',
            },
          });
        } catch (error) {
          toast.error(`Failed to upload ${file.name}`);
          throw error;
        }
      }
      const reportData = {
        ...formData,
        email: currentUser.email,
        firstName: moderatorData.firstname || '',
        lastName: moderatorData.lastname || '',
        createdAt: new Date().toISOString(),
        status: 'pending',
        fileData: uploadedFilesData,
        userType: 'moderator',
      };

      if (isEditing && selectedReport) {
        await updateDoc(doc(firestore, 'moderatorReports', selectedReport.id), reportData);
        toast.success('Report updated successfully');
      } else {
        await addDoc(collection(firestore, 'moderatorReports'), reportData);
        toast.success('Report submitted successfully');
      }

      resetForm();
      loadReports();
      setActiveTab('view');
    } catch (error) {
      toast.error('Failed to submit report. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', content: '', attachments: [] });
    setUploadedFiles([]);
    setSelectedReport(null);
    setIsEditing(false);
  };

  const handleDeleteClick = reportId => {
    setDeleteConfirm({
      isOpen: true,
      reportId,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.reportId) return;

    setIsDeleting(true);
    try {
      const report = reports.find(r => r.id === deleteConfirm.reportId);
      if (report?.fileData && Array.isArray(report.fileData)) {
        await Promise.all(
          report.fileData.map(file => {
            if (file.path) {
              const storageRef = ref(storage, file.path);
              return deleteObject(storageRef);
            }
            return Promise.resolve();
          })
        );
      }

      await deleteDoc(doc(firestore, 'moderatorReports', deleteConfirm.reportId));
      toast.success('Report deleted successfully');
      setDeleteConfirm({ isOpen: false, reportId: null });
      loadReports();
    } catch (error) {
      toast.error('Failed to delete report');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, reportId: null });
  };

  const handleEdit = report => {
    setSelectedReport(report);
    setFormData({
      title: report.title,
      content: report.content,
      attachments: report.attachments || [],
    });
    setUploadedFiles(report.fileData || []);
    setIsEditing(true);
    setActiveTab('create');
  };

  const removeFile = index => {
    const fileToRemove = uploadedFiles[index];
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(url => url !== fileToRemove.url),
    }));
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getStatusColor = status => {
    switch (status) {
      case 'approved':
        return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'rejected':
        return 'bg-slate-200 text-slate-800 border-slate-300';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-200';
    }
  };

  const FileDetailsPopover = ({ files }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = event => {
        if (popoverRef.current && !popoverRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className="relative" ref={popoverRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200"
        >
          <HiDocument className="h-3 w-3" />
          {files.length} file(s)
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 z-10 mt-2 w-72 rounded-xl border border-gray-100 bg-white p-4 shadow-lg"
            >
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-900">File Details</h4>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start gap-3 rounded-lg border border-gray-100 p-2"
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50">
                        <HiDocument className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">{file.name}</p>
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                          <span>{formatFileSize(file.size)}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <HiClock className="h-3 w-3" />
                            {new Date(file.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                      >
                        <HiArrowDownTray className="h-4 w-4" />
                      </a>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const totalPages = Math.ceil(reports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentReports = reports.slice(startIndex, endIndex);

  const PaginationControls = () => {
    return (
      <div className="mt-4 flex items-center justify-between border-t border-gray-100 px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Showing</span>
          <span className="font-medium">{startIndex + 1}</span>
          <span>to</span>
          <span className="font-medium">{Math.min(endIndex, reports.length)}</span>
          <span>of</span>
          <span className="font-medium">{reports.length}</span>
          <span>results</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <HiChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-all ${
                  currentPage === page ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
            <HiChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-black md:text-3xl">Business Advisor Reports</h1>
          <p className="mt-2 text-sm text-gray-600 md:text-base">
            Manage and submit your moderator activity reports
          </p>
        </div>

        <div className="mb-8 flex flex-wrap gap-3">
          <button
            onClick={() => setActiveTab('view')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium transition-all duration-200 ${
              activeTab === 'view'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <HiDocumentText className="h-4 w-4" />
            View Reports
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`flex items-center gap-2 rounded-xl px-6 py-3 font-medium transition-all duration-200 ${
              activeTab === 'create'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <HiPlus className="h-4 w-4" />
            Create Report
          </button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'create' ? (
            <motion.div
              key="submit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-xl"
            >
              <div className="border-b border-gray-100 bg-gradient-to-br from-blue-50 to-indigo-50 px-8 py-6">
                <h2 className="text-2xl font-bold text-gray-900">Create Moderator Report</h2>
                <p className="mt-2 text-gray-600">Share your moderator activities and insights</p>
              </div>

              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="space-y-3">
                    <label className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                      Report Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full rounded-2xl border border-gray-200 bg-white px-6 py-4 text-lg transition-all duration-200 focus:border-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-100"
                      placeholder="What's your moderator report about?"
                      required
                    />
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                      Report Content
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={e => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      rows={10}
                      className="w-full resize-none rounded-2xl border border-gray-200 bg-white px-6 py-4 text-base transition-all duration-200 focus:border-blue-300 focus:outline-none focus:ring-4 focus:ring-blue-100"
                      placeholder="Describe your moderator activities, insights, challenges, and recommendations..."
                      required
                    />
                  </div>
                  <div className="space-y-4">
                    <label className="text-sm font-semibold uppercase tracking-wide text-gray-700">
                      Attachments
                    </label>

                    <div className="relative">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                        disabled={uploading}
                      />
                      <div
                        className={`rounded-2xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 p-12 text-center transition-all duration-200 ${
                          uploading
                            ? 'border-blue-300 bg-blue-50'
                            : 'hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="space-y-4">
                          {uploading ? (
                            <motion.div
                              initial={{ scale: 0.8, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              className="flex flex-col items-center"
                            >
                              <div className="mb-4 h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
                              <p className="text-lg font-medium text-gray-700">
                                Uploading files...
                              </p>
                              <p className="text-sm text-gray-500">
                                Please wait while we process your files
                              </p>
                            </motion.div>
                          ) : (
                            <>
                              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-blue-100">
                                <HiCloudArrowUp className="h-10 w-10 text-blue-600" />
                              </div>
                              <div>
                                <p className="mb-2 text-xl font-semibold text-gray-700">
                                  Drop files here or click to upload
                                </p>
                                <p className="text-gray-500">
                                  PDF, DOC, DOCX files up to 10MB each
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <AnimatePresence>
                      {uploadedFiles.length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-3"
                        >
                          <p className="text-sm font-medium text-gray-700">
                            Uploaded Files ({uploadedFiles.length})
                          </p>
                          <div className="grid gap-3">
                            {uploadedFiles.map((file, index) => (
                              <motion.div
                                key={`${file.name}-${index}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4 shadow-sm"
                              >
                                <div className="flex items-center gap-4">
                                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100">
                                    <HiCheckCircle className="h-6 w-6 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="text-base font-semibold text-gray-900">
                                      {file.name}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {formatFileSize(file.size)} • Ready to upload
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  className="rounded-xl p-2 text-gray-400 transition-all hover:bg-red-100 hover:text-red-600"
                                >
                                  <HiTrash className="h-5 w-5" />
                                </button>
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="flex justify-end gap-4 border-t border-gray-100 pt-8">
                    {isEditing && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className="rounded-2xl px-8 py-4 font-medium text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={uploading || !formData.title.trim() || !formData.content.trim()}
                      className="rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-10 py-4 font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-xl disabled:cursor-not-allowed disabled:from-gray-300 disabled:to-gray-400"
                    >
                      {uploading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          {isEditing ? 'Updating...' : 'Submitting...'}
                        </div>
                      ) : isEditing ? (
                        'Update Report'
                      ) : (
                        'Submit Report'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-gray-900"></div>
                </div>
              ) : reports.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="rounded-2xl border border-gray-100 bg-white py-20 text-center shadow-sm"
                >
                  <HiDocumentText className="mx-auto mb-4 h-16 w-16 text-gray-300" />
                  <h3 className="mb-2 text-lg font-medium text-gray-900">No reports yet</h3>
                  <p className="mb-6 text-gray-500">
                    Create your first moderator report to get started
                  </p>
                  <button
                    onClick={() => setActiveTab('create')}
                    className="rounded-xl bg-gray-900 px-6 py-3 text-white transition-all hover:bg-gray-800"
                  >
                    Create Report
                  </button>
                </motion.div>
              ) : (
                <>
                  <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                      <div className="max-h-[70vh] overflow-y-auto md:max-h-[800px]">
                        <table className="w-full">
                          <thead className="sticky top-0 z-10 bg-gray-50/50">
                            <tr className="border-b border-gray-100">
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                                Title
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                                Date
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                                Files
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                                Status
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {currentReports.map((report, index) => {
                              return (
                                <motion.tr
                                  key={report.id}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: index * 0.05 }}
                                  className="group hover:bg-gray-50/50"
                                >
                                  <td className="px-6 py-4">
                                    <div className="max-w-xs">
                                      <h3 className="truncate text-sm font-medium text-gray-900">
                                        {report.title}
                                      </h3>
                                      <p className="mt-1 line-clamp-2 text-xs text-gray-500">
                                        {report.content}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <HiClock className="h-4 w-4 text-gray-400" />
                                      {new Date(report.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      })}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    {report.fileData &&
                                    Array.isArray(report.fileData) &&
                                    report.fileData.length > 0 ? (
                                      <FileDetailsPopover files={report.fileData} />
                                    ) : (
                                      <span className="text-sm text-gray-500">No files</span>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span
                                      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getStatusColor(report.status)}`}
                                    >
                                      {report.status.charAt(0).toUpperCase() +
                                        report.status.slice(1)}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleEdit(report)}
                                        className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                        title="Edit Report"
                                      >
                                        <HiPencil className="h-4 w-4" />
                                        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                          Edit Report
                                        </span>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteClick(report.id)}
                                        className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-slate-200 hover:text-slate-700"
                                        title="Delete Report"
                                      >
                                        <HiTrash className="h-4 w-4" />
                                        <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                          Delete Report
                                        </span>
                                      </button>
                                      {report.feedback && (
                                        <button
                                          onClick={() => setSelectedReport(report)}
                                          className="group relative rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600"
                                          title="View Feedback"
                                        >
                                          <HiChatBubbleLeftEllipsis className="h-4 w-4" />
                                          <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
                                            View Feedback
                                          </span>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </motion.tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {reports.length > itemsPerPage && <PaginationControls />}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {selectedReport && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setSelectedReport(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-2xl rounded-2xl bg-white shadow-xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-gray-100 p-6">
                  <h3 className="text-lg font-semibold text-gray-900">Report Feedback</h3>
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="rounded-lg p-2 text-gray-400 transition-all hover:bg-gray-50 hover:text-gray-600"
                  >
                    <HiXMark className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6">
                  <FeedbackForm reportId={selectedReport.id} />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Report"
          message="Are you sure you want to delete this report? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
};

export default ModeratorReports;
